import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import constants from 'scoreboard/components/constants'
import  { filenameIsExcelFile } from 'scoreboard/components/spreadsheet-file-utils'
const { REST , IMPORT_DATA_SOURCE_TYPE } = constants

function isSpreadsheet(source) {
	return ['ftp', 'google', 'zapier', 'document', 'upload', 'spreadsheet'].includes(source)
}

function isDatabase(source) {
	return source === 'database'
}

function isRest(source) {
	return source === REST
}

function hasNoDataSource(source) {
	return _.isNil(source)
}

function isRecordIdOptional(source) {
	return isSpreadsheet(source) || source === REST ||
		// datasets without a data source don't need record IDs
		_.isEmpty(source)
}

/**
 * Common subclass for imports and datasets
 *
 * @extends Backbone.Model
 */
const Importable = Backbone.Model.extend({

	initialize: function() {
		this._originalSource = this.getDataSourceMetadata('source') || 'upload'
	},

 	// use a function so each Importable gets its own dataSourceMetadata
	defaults: function() {
		/**
		 * @typedef {Object} DataSourceMetadata
		 * @property {number} id
		 *
		 * @see DataSourceMetadata.kt
		 * @see DatasetConnection.kt
		 */
		return {
			/** @type {DataSourceMetadata} */
			dataSourceMetadata: {}
		}
	},

	/**
	 * Invoked when the user selects a new input source type so when they navigate
	 * back we can show the same state as when they left.
	 *
	 * @param dataSourceType one of [ftp, google, upload, document]
	 */
	setSpreadsheetSourceSelection(dataSourceType) {
		this._lastSource = dataSourceType
	},

	/**
	 * Get's the metadata, and accepts an optional path to lookup
	 * @param [path]
	 */
	getDataSourceMetadata(path = '') {
		const metadata = this.get('dataSourceMetadata')
		return path ? _.get(metadata, path) : metadata
	},

	/**
	 * Returns whether the data source is a spreadsheet.
	 * If unknown or unset returns false.
	 */
	isSpreadsheet: function() {
		const metadata = this.getDataSourceMetadata()
		return isSpreadsheet(metadata.source)
	},

	isExcelFile: function() {
		const metadata = this.getDataSourceMetadata()

		return metadata?.source === IMPORT_DATA_SOURCE_TYPE.UPLOAD &&
			filenameIsExcelFile(metadata?.uploadFile?.name)
	},

	isRecordIdOptional: function() {
		return this.hasNoDataSource() || isRecordIdOptional(
			this.getDataSourceMetadata('source'))
	},

	hasNoDataSource: function() {
		const metadata = this.get('dataSourceMetadata')
		if (_.isNil(metadata)) return true

		const dataSource = metadata.source
		return hasNoDataSource(dataSource)
	},

	/**
	 * @returns {boolean} true if the import type has an underlying import connection
	 */
	isImportConnection: function() {
		const metadata = this.getDataSourceMetadata()
		return this.isDatabase() || _.some(['ftp', 'google'],
			t => _.get(metadata, `${t}File`))
	},

	/**
	 * Returns whether the data source is a file upload (i.e. not a spreadsheet
	 * from FTP or from the documents section)
	 */
	isFileUpload: function() {
		return this.getDataSourceMetadata('source') === 'upload'
	},

	/**
	 * Returns whether the data source is a file from the documents section
	 */
	isManagedDocument: function () {
		return this.getDataSourceMetadata('source') === 'document'
	},

	isDatabase: function() {
		return isDatabase(this.getDataSourceMetadata('source'))
	},

	/**
	 * Returns true if the data source is a Google Sheet
	 */
	isGoogleSheet: function() {
		return this.getDataSourceMetadata('source') === 'google'
	},

	/**
	 * Handles proper assignment of a 'database' or 'spreadsheet' importType value
	 * based on the existing selection (if any)
	 * @param {String} importType
	 */
	setSourceFromImportType: function(importType) {
		const metadata = this.getDataSourceMetadata()
		metadata.source = importType
		if (importType === 'spreadsheet') {
			const currentSource = this._lastSource || this._originalSource
			// When editing a database source it will be assigned to original source
			// so when they switch to spreadsheet we need to default to upload
			const useSpreadsheetDefault = _.isNil(currentSource) || currentSource === 'database'
			metadata.source = useSpreadsheetDefault ? 'upload' : currentSource
			// #32528 also clear out any database datasource info
			delete metadata.database
		}
	},

	set: function(key, val, options) {
		// extend the dataSourceMetadata rather than blowing it away
		if (_.isObject(key) && !_.isNil(this.get('dataSourceMetadata'))) {
			_.extend(this.get('dataSourceMetadata'), key.dataSourceMetadata)
			key = _.omit(key, 'dataSourceMetadata')
		}
		return Backbone.Model.prototype.set.call(this, key, val, options)
	},

	deleteFile: function() {
		// #25628 don't delete managed document revisions
		if (!this.isManagedDocument()) {
			const metadata = this.get('dataSourceMetadata')
			if (metadata.fileIds) {
				$.ajax({
					url: 'api/files',
					type: 'delete',
					data: JSON.stringify(metadata.fileIds),
					// ignore errors deleting the file
					error: _.noop
				})
				delete metadata.fileIds
				delete metadata.fileId
			} else if (metadata.fileId) {
				$.ajax({
					url: 'api/files/revisions/' + metadata.fileId,
					type: 'delete',
					// ignore errors deleting the file
					error: _.noop
				})
				delete metadata.fileId
			}
		}
	},

	/**
	 * Returns any ignored rows at the beginning of a spreadsheet
	 */
	getValidIgnoreRows: function() {
		// #31019 the only ignored rows will be the beginning consecutive
		// ignored rows before the header row
		let ignoreRows = this.getDataSourceMetadata().ignoreRows
		ignoreRows = _.sortBy(_.uniq(ignoreRows))
		let consecutiveIgnoreRows = []
		if (!_.isEmpty(ignoreRows)) {
			for (var i = 0; i <= ignoreRows.length; i++) {
				if (ignoreRows.indexOf(i) == -1) {
					break
				}
				consecutiveIgnoreRows.push(i)
			}
		}
		return consecutiveIgnoreRows
	},

	/**
	 * If a header row is present. Must be implemented by subclasses
	 */
	headerRowSelected: function() {

	},

	/**
	 * Returns the index of the selected header row.
	 * Implemented by subclasses
	 */
	headerRowIndex: function () {

	}

})

export default Importable
export { isSpreadsheet }
export { isDatabase }
export { isRest }
export { hasNoDataSource }
export { isRecordIdOptional }

Importable.__filename__ = 'imports/model/importable.js'
